/**
 * Step 4 — Connect your brain (MCP setup).
 * Key generation (shown once), endpoint URL, client snippets.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CheckCircle2, Key, Loader2, Terminal } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';
import { CodeBlock, CopyField } from '@/components/patterns/copy-field';
import { IconTile } from '@/components/patterns/icon-tile';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { MCP_FAMILIES, mcpKeysApi, workspaceApi } from '@/lib/api';
import { qk } from '@/lib/query-client';
import { mcpEndpoint } from '@/lib/utils';

interface Props {
  onDone: () => void;
}

export function StepMcp({ onDone }: Props) {
  const qc = useQueryClient();
  const [newKeyName, setNewKeyName] = useState('MCP key');
  const [revealedKey, setRevealedKey] = useState<string | null>(null);

  const { data: wsData } = useQuery({ queryKey: qk.workspace, queryFn: () => workspaceApi.get() });
  const workspace = wsData?.workspace;
  // The org-scoped URL is primary everywhere a client snippet is shown (D10)
  // — it resolves to this workspace by URL, not by whichever workspace an
  // OAuth token falls back to. Legacy shape for the instant before the
  // workspace query resolves; onboarding reaches this step seconds after the
  // workspace was created, so that instant is brief.
  const endpoint = workspace ? mcpEndpoint({ slug: workspace.slug }) : mcpEndpoint();
  const legacyEndpoint = mcpEndpoint();

  // Gates the scoped URL(s) offered below (AC22) — typically just the one
  // connector this wizard run just connected. Membership comes straight from
  // the server (`workspace.connectedFamilies`); see the field's doc comment
  // in api.ts for why that can't be inferred client-side.
  // `?? []` is a deploy-skew guard: an API response briefly missing the field
  // (rolling deploy, stale cache) must degrade to "no scoped URLs yet", never
  // crash the step.
  const scopedFamilies = workspace
    ? MCP_FAMILIES.filter((f) => (workspace.connectedFamilies ?? []).includes(f.slug))
    : [];

  const { data: keysData } = useQuery({
    queryKey: qk.mcpKeys,
    queryFn: () => mcpKeysApi.list(),
  });

  const createKey = useMutation({
    mutationFn: (input: { name: string }) => mcpKeysApi.create(input),
    onSuccess: (res) => {
      setRevealedKey(res.key);
      void qc.invalidateQueries({ queryKey: qk.mcpKeys });
      toast.success("API key created — copy it now, it won't be shown again");
    },
    onError: () => toast.error('Failed to create API key'),
  });

  // Canonical snippet shapes per docs/connect.md.
  const claudeCodeSnippet = `claude mcp add --transport http brain ${endpoint} \\\n  --header "Authorization: Bearer ${revealedKey ?? '<YOUR_API_KEY>'}"`;

  const cursorSnippet = JSON.stringify(
    {
      mcpServers: {
        brain: {
          url: endpoint,
          headers: { Authorization: `Bearer ${revealedKey ?? '<YOUR_API_KEY>'}` },
        },
      },
    },
    null,
    2,
  );

  const hasKeys = (keysData?.keys.length ?? 0) > 0;

  return (
    <div className="space-y-6">
      {/* Endpoint */}
      <div className="rounded-xl border border-border p-8">
        <IconTile tone="dark">
          <Terminal className="h-5 w-5" />
        </IconTile>
        <h2 className="mt-4 text-lg font-semibold">Connect your brain</h2>
        <p className="mt-1.5 text-[14px] text-muted-foreground">
          Your Brain MCP server is live. Connect Claude Code, Cursor, or claude.ai to start
          searching your group chat from any AI client.
        </p>

        <div className="mt-6 space-y-1.5">
          <Label>Workspace endpoint</Label>
          <CopyField mono value={endpoint} />
        </div>

        {scopedFamilies.length > 0 && workspace && (
          <div className="mt-4 space-y-2">
            <p className="text-[13px] text-muted-foreground">
              Add the workspace endpoint above, or add the connectors you actually search below —
              not both. Every endpoint you register adds its tools to your AI client&apos;s context
              on every turn.
            </p>
            <div className="space-y-2.5">
              {scopedFamilies.map((f) => (
                <div key={f.slug} className="space-y-1">
                  <span className="text-[13px] font-medium text-label">{f.label}</span>
                  <CopyField mono value={mcpEndpoint({ slug: workspace.slug, family: f.slug })} />
                </div>
              ))}
            </div>
          </div>
        )}

        <details className="group mt-4 rounded-md border border-border-subtle px-3.5 py-2.5">
          <summary className="cursor-pointer text-[13px] text-muted-foreground">
            Already connected elsewhere? <code className="font-mono">/mcp</code> keeps working
          </summary>
          <p className="mt-2 text-[13px] text-muted-foreground">
            The original workspace-wide endpoint (
            <code className="font-mono">{legacyEndpoint}</code>) still works — it infers the
            workspace from the key instead of the URL. New connections should use the workspace
            endpoint above.
          </p>
        </details>
      </div>

      {/* Key generation */}
      <div className="rounded-xl border border-border p-8">
        <h3 className="text-base font-semibold">API key</h3>
        <p className="mt-0.5 text-[13.5px] text-muted-foreground">
          Create an API key for Claude Code or Cursor. The key is shown exactly once — copy it now.
        </p>

        <div className="mt-5">
          {revealedKey ? (
            <Alert variant="success">
              <Key className="h-4 w-4" />
              <AlertTitle>Your API key — copy it now</AlertTitle>
              <AlertDescription className="space-y-2">
                <p className="text-[13px] text-success-foreground/80">
                  This key will not be displayed again. Store it somewhere safe.
                </p>
                <CopyField mono value={revealedKey} />
              </AlertDescription>
            </Alert>
          ) : (
            <div className="space-y-4">
              <div className="space-y-1.5">
                <Label htmlFor="mcp-key-name">Key name</Label>
                <Input
                  id="mcp-key-name"
                  placeholder="Key name"
                  value={newKeyName}
                  onChange={(e) => setNewKeyName(e.target.value)}
                />
              </div>
              <Button
                disabled={createKey.isPending || !newKeyName.trim()}
                onClick={() => createKey.mutate({ name: newKeyName.trim() })}
              >
                {createKey.isPending ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <>
                    <Key className="h-4 w-4" />
                    Create key
                  </>
                )}
              </Button>
            </div>
          )}
        </div>
      </div>

      {/* Client setup snippets */}
      {(revealedKey || hasKeys) && (
        <div className="rounded-xl border border-border p-8">
          <h3 className="text-base font-semibold">Client setup</h3>
          <div className="mt-5 space-y-5">
            {/* Claude Code */}
            <CodeBlock label="Claude Code — CLI">{claudeCodeSnippet}</CodeBlock>

            {/* Cursor */}
            <CodeBlock label="Cursor — .cursor/mcp.json">{cursorSnippet}</CodeBlock>

            {/* claude.ai */}
            <div className="space-y-2">
              <div className="font-mono text-[11px] uppercase tracking-[0.06em] text-faint">
                claude.ai — Custom connector (OAuth)
              </div>
              <p className="text-[13.5px] text-muted-foreground">
                In claude.ai, open <strong>Settings → Connectors → Add custom connector</strong> and
                enter your endpoint URL. claude.ai discovers OAuth automatically and redirects you
                to Brain to sign in — no API key needed.
              </p>
              <CopyField mono value={endpoint} />
            </div>
          </div>
        </div>
      )}

      {/* Done state */}
      <Alert variant="success">
        <CheckCircle2 className="h-4 w-4" />
        <AlertTitle>You&apos;re all set</AlertTitle>
        <AlertDescription>
          Send a message in your WhatsApp group, then ask your assistant about it. Brain indexes new
          messages within seconds.
        </AlertDescription>
      </Alert>

      <Button size="cta" className="w-full" onClick={onDone} data-testid="finish-onboarding">
        Finish — go to dashboard
      </Button>
    </div>
  );
}
